import { useState } from 'react';
import { Alert } from '../../../components/ui/Alert';
import { Button } from '../../../components/ui/Button';
import { Card, CardHeader } from '../../../components/ui/Card';
import { Modal } from '../../../components/ui/Modal';
import { ErrorState, LoadingState } from '../../../components/ui/States';
import { TabBar } from '../../../components/ui/Tabs';
import { useBucket, useBucketOptions, useChecklist, useTeam, useUpdateBucket } from '../../../hooks/usePilots';
import type { BucketDetail, BucketOptions, TeamMember } from '../../../api/pilots';
import { formatDate, formatDateTime } from '../outreach/format';
import { PeopleTable, inputClass } from './PeopleTable';
import { AdsStageBadge, BucketStatusBadge, pct } from './format';
import { MoreMenu, NextStep, TableSkeleton } from './parts';

// ── Checklist ───────────────────────────────────────────────────────────────

/** "waiting on Jo" once set; until then a quiet "assign" that opens the picker, so an empty row says nothing. */
function OwnerPicker({ value, team, disabled, onChange }: { value: string | null; team: TeamMember[]; disabled: boolean; onChange: (email: string | null) => void }) {
  const [open, setOpen] = useState(false);
  if (!value && !open) {
    return <button className="ml-auto text-body-xs text-text-muted hover:text-text-primary hover:underline" onClick={() => setOpen(true)}>assign</button>;
  }
  return (
    <select autoFocus={open && !value} className="ml-auto w-32 rounded-md border border-border-light bg-surface-primary px-2 py-1 text-body-xs text-text-secondary" value={value ?? ''} disabled={disabled} onChange={(e) => { onChange(e.target.value || null); if (!e.target.value) setOpen(false); }} onBlur={() => { if (!value) setOpen(false); }}>
      <option value="">waiting on…</option>
      {team.map((m) => <option key={m.email} value={m.email}>{m.name}</option>)}
    </select>
  );
}

function Checklist({ bucket, isAdmin, team }: { bucket: BucketDetail; isAdmin: boolean; team: TeamMember[] }) {
  const { add, update, remove } = useChecklist(bucket.id);
  const [label, setLabel] = useState('');
  const error = add.error ?? update.error ?? remove.error;
  const nameOf = (email: string | null) => team.find((m) => m.email === email)?.name ?? email?.split('@')[0] ?? null;
  return (
    <Card padding="none">
      <div className="p-4 pb-0">
        <CardHeader title={`Launch checklist · ${bucket.checklist.done}/${bucket.checklist.total}`} />
      </div>
      <div className="p-4 pt-0 space-y-3">
        <ul className="divide-y divide-border-light">
          {bucket.checklistItems.map((item) => (
            <li key={item.id} className="group flex items-center gap-3 py-2">
              <input type="checkbox" checked={!!item.doneAt} disabled={update.isPending} onChange={(e) => update.mutate({ itemId: item.id, done: e.target.checked })} />
              <span className={item.doneAt ? 'text-body-sm text-text-tertiary line-through' : 'text-body-sm text-text-primary'}>{item.label}</span>
              {item.doneAt ? (
                <span className="text-body-xs text-text-muted">{nameOf(item.doneBy)} · {formatDate(item.doneAt)}</span>
              ) : isAdmin ? (
                <OwnerPicker value={item.ownerEmail} team={team} disabled={update.isPending} onChange={(ownerEmail) => update.mutate({ itemId: item.id, ownerEmail })} />
              ) : (
                item.ownerEmail && <span className="ml-auto text-body-xs text-text-tertiary">waiting on {nameOf(item.ownerEmail)}</span>
              )}
              {isAdmin && <button className="text-body-xs text-text-muted opacity-0 hover:text-error focus:opacity-100 group-hover:opacity-100" aria-label={`Remove “${item.label}”`} onClick={() => remove.mutate(item.id)}>×</button>}
            </li>
          ))}
        </ul>
        {isAdmin && (
          <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (label.trim()) add.mutate(label.trim(), { onSuccess: () => setLabel('') }); }}>
            <input className={inputClass} placeholder="Add a step for this bucket" value={label} onChange={(e) => setLabel(e.target.value)} />
            <Button type="submit" size="sm" variant="secondary" isLoading={add.isPending} disabled={!label.trim()}>Add</Button>
          </form>
        )}
        {error && <p className="text-body-xs text-error">Not saved: {error.message}</p>}
      </div>
    </Card>
  );
}

// ── Links: lemlist, ads, resources ──────────────────────────────────────────

function LinksModal({ bucket, isOpen, onClose }: { bucket: BucketDetail; isOpen: boolean; onClose: () => void }) {
  const options = useBucketOptions(isOpen);
  const update = useUpdateBucket(bucket.id);
  const [lemlist, setLemlist] = useState(new Set(bucket.lemlistCampaigns.map((c) => c.id)));
  const [ads, setAds] = useState(new Set(bucket.adCampaigns.map((a) => `${a.platform}:${a.campaignId}`)));
  const [resources, setResources] = useState(new Set(bucket.resourceItems.map((r) => r.id)));
  const toggle = (set: Set<string>, setter: (s: Set<string>) => void, key: string) => { const next = new Set(set); if (next.has(key)) next.delete(key); else next.add(key); setter(next); };
  const save = () =>
    update.mutate(
      {
        lemlistCampaignIds: [...lemlist],
        adCampaigns: [...ads].map((k) => { const i = k.indexOf(':'); return { platform: k.slice(0, i), campaignId: k.slice(i + 1) }; }),
        resourceIds: [...resources],
      },
      { onSuccess: onClose },
    );
  const Picker = <T,>({ title, hint, items, keyOf, label, set, setter }: { title: string; hint: string; items: T[]; keyOf: (t: T) => string; label: (t: T) => string; set: Set<string>; setter: (s: Set<string>) => void }) => (
    <div className="space-y-1">
      <p className="text-body-xs font-medium text-text-secondary">{title}</p>
      <p className="text-body-xs text-text-muted">{hint}</p>
      <div className="max-h-44 overflow-y-auto rounded-md border border-border-light p-2">
        {items.length === 0 && <p className="text-body-xs text-text-muted">Nothing synced yet.</p>}
        {items.map((item) => { const k = keyOf(item); return (
          <label key={k} className="flex items-center gap-2 py-0.5 text-body-sm text-text-primary">
            <input type="checkbox" checked={set.has(k)} onChange={() => toggle(set, setter, k)} />{label(item)}
          </label>
        ); })}
      </div>
    </div>
  );
  const o: BucketOptions | undefined = options.data;
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Link campaigns and resources" description="Only real lemlist campaigns, ad campaigns and materials the OS already knows, so nothing is typed by hand." size="xl">
      {options.isLoading && <LoadingState rows={2} />}
      {options.error && <Alert variant="error">Could not load the options: {options.error.message}</Alert>}
      {o && (
        <div className="space-y-4">
          <Picker title="lemlist campaigns" hint="Where this bucket's outreach runs. Several if each rep has their own." items={o.lemlistCampaigns} keyOf={(c) => c.id} label={(c) => `${c.name ?? c.id}${c.status ? ` (${c.status})` : ''}`} set={lemlist} setter={setLemlist} />
          <Picker title="Ad campaigns" hint="The ads Lea built for this bucket. Created and published then come from the platform." items={o.adCampaigns} keyOf={(a) => `${a.platform}:${a.campaignId}`} label={(a) => `${a.platform} · ${a.name ?? a.campaignId}${a.status ? ` (${a.status})` : ''}`} set={ads} setter={setAds} />
          <Picker title="Resources to send" hint="From Resources → Campaign materials. Create a material there first if it is missing; it can exist without a link until it is ready." items={o.resources} keyOf={(r) => r.id} label={(r) => `${r.title} · ${r.kind}${r.available ? '' : ' (no link yet)'}`} set={resources} setter={setResources} />
          {update.error && <Alert variant="error">Not saved: {update.error.message}</Alert>}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button onClick={save} isLoading={update.isPending}>Save</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

/** lemlist and ad campaigns together: where the outreach runs. */
function CampaignsCard({ bucket }: { bucket: BucketDetail }) {
  // One status for every lemlist campaign is said once, not on each row.
  const statuses = new Set(bucket.lemlistCampaigns.map((c) => c.status ?? ''));
  const sharedStatus = bucket.lemlistCampaigns.length > 1 && statuses.size === 1 ? [...statuses][0] : null;
  return (
    <Card padding="none">
      <div className="p-4 pb-0">
        <CardHeader title="Campaigns" action={<AdsStageBadge stage={bucket.adsStage} />} />
      </div>
      {sharedStatus && <p className="px-4 text-body-xs text-text-tertiary">lemlist · {sharedStatus}</p>}
      <ul className="divide-y divide-border-light p-4 pt-0 text-body-sm">
        {bucket.lemlistCampaigns.map((c) => (
          <li key={c.id} className="flex items-center gap-3 py-2">
            <a href={c.url} target="_blank" rel="noreferrer" className="font-medium text-accent hover:underline">{c.name ?? c.id} ↗</a>
            {!sharedStatus && <span className="ml-auto text-body-xs text-text-tertiary">{`lemlist${c.status ? ` · ${c.status}` : ''}`}</span>}
          </li>
        ))}
        {bucket.adCampaigns.map((a) => (
          <li key={`${a.platform}:${a.campaignId}`} className="flex items-center gap-3 py-2">
            <span className="font-medium text-text-primary">{a.name ?? a.campaignId}</span>
            {a.silent && <span className="text-body-xs text-warning-dark">shown to nobody in 7 days</span>}
            <span className="ml-auto text-body-xs text-text-tertiary">{a.platform} · {a.platformStatus ? a.platformStatus.toLowerCase() : 'not synced'}</span>
          </li>
        ))}
        {bucket.lemlistCampaigns.length + bucket.adCampaigns.length === 0 && <li className="py-2 text-text-tertiary">None linked yet.</li>}
      </ul>
    </Card>
  );
}

function ResourcesCard({ bucket }: { bucket: BucketDetail }) {
  return (
    <Card padding="none">
      <div className="p-4 pb-0">
        <CardHeader title={`Resources · ${bucket.resources.available}/${bucket.resources.total}`} />
      </div>
      <ul className="divide-y divide-border-light p-4 pt-0 text-body-sm">
        {bucket.resourceItems.map((r) => (
          <li key={r.id} className="flex items-center gap-3 py-2">
            {r.url ? <a href={r.url} target="_blank" rel="noreferrer" className="font-medium text-accent hover:underline">{r.title} ↗</a> : <span className="font-medium text-text-primary">{r.title}</span>}
            <span className={r.available ? 'ml-auto text-body-xs text-text-tertiary' : 'ml-auto text-body-xs text-warning-dark'}>{r.available ? r.kind : 'no link yet'}</span>
          </li>
        ))}
        {bucket.resourceItems.length === 0 && <li className="py-2 text-text-tertiary">Nothing planned yet.</li>}
      </ul>
    </Card>
  );
}

// ── Results: one line, the meetings behind a click ──────────────────────────

const MATCH_LABEL = { person: 'person in the bucket', domain: 'someone else at the company', manual: 'ticked by hand' } as const;

function MeetingsModal({ bucket, isOpen, onClose }: { bucket: BucketDetail; isOpen: boolean; onClose: () => void }) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Meetings · ${bucket.funnel.meetings}`} description="From Calendly, the calendars in Attio, and the Meeting tick on a person." size="xl">
      <ul className="divide-y divide-border-light">
        {bucket.meetingsList.map((m) => (
          <li key={`${m.email ?? m.name}-${m.at}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-body-sm">
            <span className="font-medium text-text-primary">{m.name ?? m.email}</span>
            {m.company && <span className="text-text-secondary">{m.company}</span>}
            <span className="text-body-xs text-text-tertiary">{MATCH_LABEL[m.match]}</span>
            <span className="ml-auto text-body-xs tabular-nums text-text-tertiary">{formatDateTime(m.meetingStartAt)} · {m.source}</span>
          </li>
        ))}
      </ul>
    </Modal>
  );
}

function ResultsLine({ bucket }: { bucket: BucketDetail }) {
  const [meetings, setMeetings] = useState(false);
  const f = bucket.funnel;
  // Each step's share is of the step before it: reached of people, accepted and replied of reached.
  // An invite can only be accepted once someone was reached, so "1 accepted · 0%" of 354 people hid a 1-in-6 rate.
  const part = (n: number, label: string, title: string, of: number, ofLabel: string) => (
    <span title={`${title}${of > 0 && n > 0 ? ` · ${pct(n, of)} of ${ofLabel}` : ''}`}>
      <span className="tabular-nums font-medium text-text-primary">{n}</span> {label}
      {of > 0 && n > 0 && <span className="text-text-muted"> {pct(n, of)} of {ofLabel}</span>}
    </span>
  );
  // A bucket that has not launched has no touches yet: an all-zero strip says nothing (the tab already counts the people).
  const launched = bucket.status !== 'planned' || bucket.startedAt !== null;
  if (!launched && f.reached + f.accepted + f.discussions + f.meetings === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-body-sm text-text-secondary">
      <span><span className="tabular-nums font-medium text-text-primary">{f.people}</span> {f.people === 1 ? 'person' : 'people'}</span>
      {part(f.reached, 'reached', 'At least one real touch in lemlist', f.people, 'people')}
      {part(f.accepted, 'accepted', 'LinkedIn invite accepted', f.reached, 'reached')}
      {part(f.discussions, 'replied', 'Replied at least once', f.reached, 'reached')}
      {bucket.meetingsList.length > 0
        ? <button className="hover:underline" onClick={() => setMeetings(true)}><span className="tabular-nums font-medium text-text-primary">{f.meetings}</span> {f.meetings === 1 ? 'meeting' : 'meetings'}</button>
        : <span><span className="tabular-nums font-medium text-text-primary">{f.meetings}</span> {f.meetings === 1 ? 'meeting' : 'meetings'}</span>}
      {meetings && <MeetingsModal bucket={bucket} isOpen={meetings} onClose={() => setMeetings(false)} />}
    </div>
  );
}

// ── The page ────────────────────────────────────────────────────────────────

type Tab = 'people' | 'setup';

export function BucketDetailView({ id, isAdmin, onBack }: { id: string; isAdmin: boolean; onBack: () => void }) {
  const query = useBucket(id);
  const team = useTeam();
  const update = useUpdateBucket(id);
  const [linking, setLinking] = useState(false);
  const [tab, setTab] = useState<Tab>('people');

  if (query.isLoading) {
    return (
      <Card padding="md">
        <TableSkeleton columns={[{ label: 'Person' }, { label: 'Last touch' }]} rows={8} minWidth={760} />
      </Card>
    );
  }
  if (query.error) return <ErrorState title="Couldn’t load the bucket" error={query.error} onRetry={() => query.refetch()} />;
  const bucket = query.data;
  if (!bucket) return null;

  const openSteps = bucket.checklist.total - bucket.checklist.done;
  const launch = () => {
    if (openSteps > 0 && !window.confirm(`${openSteps} checklist step${openSteps === 1 ? '' : 's'} still open. Launch anyway?`)) return;
    update.mutate({ status: 'active' });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={onBack} className="text-body-sm text-text-tertiary hover:text-text-primary" aria-label="All buckets">←</button>
          <h2 className="text-heading-md font-light text-text-primary">{bucket.name}</h2>
          <span title={bucket.startedAt ? `Launched ${formatDate(bucket.startedAt)}` : undefined}><BucketStatusBadge status={bucket.status} /></span>
          {isAdmin && (
            <div className="ml-auto flex items-center gap-1">
              {bucket.status === 'planned' && <Button size="sm" isLoading={update.isPending} onClick={launch}>Launch</Button>}
              <MoreMenu items={[
                { label: 'Link campaigns & resources', onClick: () => setLinking(true) },
                ...(bucket.status === 'active' ? [{ label: 'Mark done', onClick: () => update.mutate({ status: 'done' }) }] : []),
                ...(bucket.status === 'done' ? [{ label: 'Reopen', onClick: () => update.mutate({ status: 'active' }) }] : []),
              ]} />
            </div>
          )}
        </div>
        <NextStep blocking={bucket.blocking} quiet={bucket.status !== 'active'} />
        {update.error && <Alert variant="error">Not saved: {update.error.message}</Alert>}
      </div>

      <TabBar
        tabs={[
          { id: 'people', label: `People · ${bucket.funnel.people}` },
          { id: 'setup', label: `Setup · ${bucket.checklist.done}/${bucket.checklist.total}` },
        ]}
        active={tab}
        onSelect={setTab}
        breadcrumb={false}
      />

      {tab === 'people' ? (
        <div className="space-y-3">
          <ResultsLine bucket={bucket} />
          <PeopleTable bucketId={bucket.id} companies={bucket.companyList} isAdmin={isAdmin} launched={bucket.status !== 'planned' || bucket.startedAt !== null} />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <Checklist bucket={bucket} isAdmin={isAdmin} team={team.data?.team ?? []} />
          <CampaignsCard bucket={bucket} />
          <ResourcesCard bucket={bucket} />
        </div>
      )}

      {isAdmin && linking && <LinksModal bucket={bucket} isOpen={linking} onClose={() => setLinking(false)} />}
    </div>
  );
}
